/*  iceberg-ui  */
import { Box } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { Typography } from '@mui/material';

/*  Shared  */
import {
    Header,
    ManagedTableV2ContextProvider,
    ManagedTableV2UI
} from 'application/Shared/Components/Table';
import { ColumnDef } from 'application/Shared/Components/Table/types';
import { TABLENAMES } from 'application/Shared/Constants';
import { Breach as BreachType } from 'application/Shared/types/breach';

/* Objects  */
import { Application } from 'objects/Application';
import { Breach, BreachDetail } from 'objects/Breach';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';

const NoResultsRender = () => {
    const { t } = useTranslation();
    return (
        <Typography sx={{ padding: '12px' }}>
            {t('VendorIncident.no_results')}
        </Typography>
    );
};

const columns: ColumnDef<BreachType, string>[] = [
    {
        accessorKey: 'dateAnnounced',
        cell: ({ row }) => (
            <Box style={{ minHeight: 185 }}>
                <Breach data={row.original}>
                    <Application data={row.original.application}>
                        <BreachDetail hideClientsImpactedLine />
                    </Application>
                </Breach>
            </Box>
        ),
        enableColumnFilter: false,
        enableHiding: false,
        header: Header,
        id: 'dateAnnounced',
        name: 'Common.application',
        size: 300,
        testId: 'application.name'
    }
];

/**
 * Component that displays vendor incidents for a specific application
 * Renders a managed table showing incident details for the given application
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.applicationId - ID of the application to show incidents for
 * @returns {JSX.Element} Vendor incidents table component
 */
export const AppDetailsVendorIncidents = ({
    applicationId
}: {
    applicationId: string;
}) => {
    const { t } = useTranslation();

    const parametersBreaches = {
        applicationId,
        includeApplication: true,
        includeClientsImpacted: true,
        size: -1
    };

    return (
        <Box p="24px">
            <Typography
                color="textSecondary"
                fontWeight={600}
                sx={{ paddingBottom: 0 }}
            >
                {t('Common.vendor_incidents')}
            </Typography>
            <ManagedTableV2ContextProvider
                baseParameters={parametersBreaches}
                columns={columns}
                getRowId="id"
                ignoreSearchParams
                rowsApi="/breach"
                mode="client"
                rowsApiPreload
                rowsPerPage={5}
                searchEnabled={false}
                sortDirection="DESC"
                sortKey="dateAnnounced"
                tableName={TABLENAMES.APP_DETAILS_VENDOR_INCIDENTS}
            >
                <ManagedTableV2UI
                    noResultsRender={<NoResultsRender />}
                    renderFooter
                    renderHeaderRow={false}
                />
            </ManagedTableV2ContextProvider>
        </Box>
    );
};
